import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, CloseCircleOutlined } from "@ant-design/icons";
import {
  getDriverById,
  updateDriverStatus,
} from "../../../../API/FleetManager/Drivers";
import {
  Button,
  Dropdown,
  Switch,
  notification,
  message,
  Popconfirm,
  InputNumber,
  Input,
} from "antd";
import {
  LockOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import vehicleIcon from "../../../../Assets/Icons/ad_group.svg";
import fuelIcon from "../../../../Assets/Icons/local_gas_station.svg";
import driverIcon from "../../../../Assets/Icons/id_card.svg";
import dobIcon from "../../../../Assets/Icons/calendar_month.svg";
import phoneIcon from "../../../../Assets/Icons/call.svg";
import emailIcon from "../../../../Assets/Icons/mail.svg";
import licenseIcon from "../../../../Assets/Icons/smart_card_reader.svg";
import docIcon from "../../../../Assets/Icons/document.svg";
import pdfIcon from "../../../../Assets/Icons/pdf.svg";
import GenericModal from "../../../../Components/Modals/GenericModal";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPhone = (phone) => {
  if (!phone) return "-";
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const DriverDetail = () => {
  const { canEdit } = usePageAccess("drivers");
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFuelModalOpen, setIsFuelModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInactivated, setIsInactivated] = useState(false);

  const [driver, setDriver] = useState({
    name: "-",
    avatar: null,
    email: "-",
    dob: "-",
    phone: "-",
    jobTitle: "-",
    address: "-",
    licenseNo: "-",
    licenseIssuingDate: "-",
    licenseExpiry: "-",
    fuelCredits: "-",
    assignedVehicle: "-",
    status: "-",
    startDate: "-",
  });

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No driver selected. Please open this page from the Drivers list.",
      );
      return;
    }

    const fetchDriver = async () => {
      try {
        const res = await getDriverById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          setDriver({
            name: data.name || "-",
            avatar: resolveAvatarUrl(data.avatar),
            email: data.email || "-",
            dob: formatDate(data.dob),
            phone: formatPhone(data.phone_number),
            jobTitle: data.job_title || "-",
            address: data.address || "-",
            licenseNo: data.license_number || "-",
            licenseIssuingDate: data.license_issuing_date || "-",
            licenseExpiry: data.license_expiry_date || "-",
            fuelCredits:
              data.fuel_credit !== undefined && data.fuel_credit !== null
                ? `$${parseFloat(data.fuel_credit).toFixed(2)}`
                : "-",
            assignedVehicle: data.vehicle
              ? [data.vehicle.make, data.vehicle.model]
                  .filter(Boolean)
                  .join(" ")
              : "-",
            status: capitalize(data.status) || "-",
            startDate: formatDate(data.start_date),
          });
          setIsInactivated(data.status === "suspended");
          setReason(data.reason || "");

          if (Array.isArray(data.documents)) {
            setDocuments(
              data.documents.map((doc) => {
                const label = (doc.category || "Document").replace(
                  /_/g,
                  " ",
                );
                const displayLabel =
                  label.charAt(0).toUpperCase() + label.slice(1);

                return {
                  key: String(doc.id),
                  name: doc.file
                    ? doc.file.split("/").pop()
                    : displayLabel,
                  category: displayLabel,
                  date: formatDate(doc.uploaded_at),
                  expiryDate: formatDate(doc.expiry_date),
                  fileUrl: resolveAvatarUrl(doc.file),
                };
              }),
            );
          }
        } else {
          message.error(res?.data?.message || "Failed to load driver.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load driver.",
        );
      }
    };

    fetchDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedId]);
  const [reason, setReason] = useState("");

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditDriver = () => {
    navigate("/edit-driver", { state: { id: passedId } });
  };

  const handleView = (record) => {
    if (!record?.fileUrl) return;

    const lowerName = record.name.toLowerCase();

    if (lowerName.endsWith(".pdf")) {
      window.open(record.fileUrl, "_blank", "noopener,noreferrer");
    } else {
      const link = document.createElement("a");
      link.href = record.fileUrl;
      link.download = record.name; // forces download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteDocument = (record) => {
    setSelectedRow(record);
    setIsDeleteModalOpen(true);
  };

  const handleToggleActivity = () => {
    if (!isInactivated) {
      setIsFuelModalOpen(true);
    }
  };

  const handleConfirmActivity = async (enteredReason) => {
    try {
      const res = await updateDriverStatus(passedId, {
        status: "suspended",
        reason: enteredReason,
      });

      if (Math.floor(res?.status / 100) === 2) {
        setIsInactivated(true);
        setReason(enteredReason);
        setIsFuelModalOpen(false);
        setDriver((prev) => ({ ...prev, status: "Suspended" }));

        notification.success({
          message: "Driver Inactivated",
          description: "Driver has been inactivated successfully!",
          placement: "topRight",
        });
      } else {
        message.error(res?.data?.message || "Failed to inactivate driver.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to inactivate driver.",
      );
    }
  };

  const handleActivate = async () => {
    try {
      const res = await updateDriverStatus(passedId, { status: "active" });

      if (Math.floor(res?.status / 100) === 2) {
        setIsInactivated(false);
        setReason("");
        setDriver((prev) => ({ ...prev, status: "Active" }));
        notification.success({
          message: "Driver Activated",
          description: "Driver has been activated successfully!",
          placement: "topRight",
        });
      } else {
        message.error(res?.data?.message || "Failed to activate driver.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to activate driver.",
      );
    }
  };
  const columns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      enableSearch: true,
      render: (name) => {
        let icon = docIcon;
        const lowerName = name.toLowerCase();

        if (lowerName.endsWith(".pdf")) icon = pdfIcon;
        else if (
          lowerName.endsWith(".doc") ||
          lowerName.endsWith(".docx") ||
          lowerName.endsWith(".coc")
        )
          icon = docIcon;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={icon} alt="doc icon" style={{ width: 20, height: 20 }} />
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: [
        { text: "License", value: "License" },
        { text: "Tax", value: "Tax" },
        { text: "Insurance", value: "Insurance" },
      ],
      onFilter: (value, record) => record.category === value,
      render: (category) => {
        let badgeClass = "";

        switch (category.toLowerCase()) {
          case "license":
            badgeClass = "badge-license";
            break;
          case "tax":
            badgeClass = "badge-tax";
            break;
          case "insurance":
            badgeClass = "badge-insurance";
            break;
          default:
            badgeClass = "badge-default";
        }

        return (
          <span className={`badge-category ${badgeClass}`}>{category}</span>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Verified", value: "Verified" },
        { text: "Active", value: "Active" },
        { text: "Pending", value: "Pending" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        let statusClass = "";

        if (status === "Verified") statusClass = "status-verified";
        else if (status === "Active") statusClass = "status-active";
        else statusClass = "status-pending";

        return <span className={statusClass}>{status}</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          canEdit && {
            key: "delete",
            label: "Remove",
            style: { color: "#DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => handleDeleteDocument(record),
          },
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            style: { color: "#000000", fontWeight: 500 },
            onClick: () => handleView(record),
          },
        ].filter(Boolean);
        return (
          <>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 24,
                  height: 24,
                }}
              >
                <BsThreeDotsVertical
                  style={{ fontSize: 20, color: "#4F46E5" }}
                />
              </span>
            </Dropdown>
          </>
        );
      },
    },
  ];

  const [documents, setDocuments] = useState([]);

  return (
    <div className="vehicle-detail">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="header-wrapper">
        <div className="title">Driver Detail</div>
        <div className="btns">
          {canEdit && (
            <div>
              {!isInactivated ? (
                <Button
                  className="fuel-btn"
                  icon={<CloseCircleOutlined />}
                  onClick={handleToggleActivity}
                >
                  Deactivate Driver
                </Button>
              ) : (
                <Popconfirm
                  title="Are you sure you want to activate driver"
                  onConfirm={handleActivate}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    className="fuel-btn enabled"
                    icon={<LockOutlined />}
                    style={{
                      color: "#16a34a",
                      backgroundColor: "#16a34a1a",
                    }}
                  >
                    Active
                  </Button>
                </Popconfirm>
              )}
            </div>
          )}

          {canEdit && (
            <div>
              <Button
                icon={<EditOutlined />}
                type="primary"
                className="edit-btn"
                onClick={handleEditDriver}
              >
                Edit Driver
              </Button>
            </div>
          )}
        </div>
      </div>
      {isInactivated && (
        <div className="locked-info">
          <div className="label">
            Inactive Reason
            <div className="reason"> {reason}</div>
          </div>
        </div>
      )}
      <GenericModal
        open={isFuelModalOpen}
        title="Deactivate Driver"
        subtitle={` Are you sure you want to Inactive of  ${selectedRow?.driverName}?
         This will immediately decline all active card transactiion. `}
        showReason={true}
        confirmText="Confirm Deactivate"
        cancelText="Go Back"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsFuelModalOpen(false)}
        onConfirm={handleConfirmActivity}
      />

      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              {driver.avatar ? (
                <img
                  src={driver.avatar}
                  alt={driver.name}
                  className="profile-avatar"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <div
                  className="profile-avatar"
                  style={{ background: getAvatarColor(driver.name) }}
                >
                  {getInitials(driver.name)}
                </div>
              )}
            </div>

            <div className="profile-body">
              <div className="company-name">{driver.name}</div>
              <div className="company-id">{driver.licenseNo}</div>

              <div
                style={{ display: "flex", gap: 8, justifyContent: "center" }}
              >
                <span
                  className={`badge ${driver.status === "Active" ? "badge-active" : "badge-inactive"}`}
                >
                  {driver.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="info">
            <div className="info-card-right">
              <div className="info-grid-right">
                <div className="item">
                  <div className="card-wrapper">
                    <div className="icon">
                      <img src={driverIcon} alt="driver Icon" />
                    </div>
                    <div className="info-label">Driver Name</div>
                  </div>
                  <div className="info-value">{driver.name}</div>
                </div>
                <div className="item">
                  <div className="card-wrapper">
                    <div className="icon">
                      <img src={dobIcon} alt="dob Icon" />
                    </div>
                    <div className="info-label">Date of Birth</div>
                  </div>
                  <div className="info-value">{driver.dob}</div>
                </div>
                <div className="item">
                  <div className="card-wrapper">
                    <div className="icon">
                      <img src={phoneIcon} alt="phone Icon" />
                    </div>
                    <div className="info-label">Phone Number</div>
                  </div>
                  <div className="info-value">{driver.phone}</div>
                </div>
                <div className="item">
                  <div className="card-wrapper">
                    <div className="icon">
                      <img src={emailIcon} alt="email Icon" />
                    </div>
                    <div className="info-label">Email Address</div>
                  </div>
                  <div className="info-value">{driver.email}</div>
                </div>
                <div className="item">
                  <div className="card-wrapper">
                    <div className="icon">
                      <img src={vehicleIcon} alt="vehicle Icon" />
                    </div>
                    <div className="info-label">Assigned Vehicle</div>
                  </div>
                  <div className="info-value">{driver.assignedVehicle}</div>
                </div>
                <div className="item">
                  <div className="card-wrapper">
                    <div className="icon">
                      <img src={dobIcon} alt="date Icon" />
                    </div>
                    <div className="info-label">Start Date </div>
                  </div>
                  <div className="info-value">{driver.startDate}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="info">
        <div className="info-card">
          <div className="info-grid">
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={licenseIcon} alt="license Icon" />
                </div>
                <div className="info-label">License Number</div>
              </div>
              <div className="info-value">{driver.licenseNo}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={dobIcon} alt="state Icon" />
                </div>
                <div className="info-label">Issuing Date</div>
              </div>
              <div className="info-value">{driver.licenseIssuingDate}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={dobIcon} alt="date Icon" />
                </div>
                <div className="info-label">Expiry Date</div>
              </div>
              <div className="info-value">{driver.licenseExpiry}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={fuelIcon} alt="fuel Icon" />
                </div>
                <div className="info-label">Fuel Credits</div>
              </div>
              <div className="info-value">{driver.fuelCredits}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="document-table-fleet">
        <GenericDocumentManagerTable
          initialDocuments={documents}
          showTableOnly={true}
          hideRemoveAction={true}
        />

        <GenericModal
          open={isDeleteModalOpen}
          title="Remove Document"
          subtitle={`Are you sure you want to remove ${selectedRow?.name}?`}
          highlightText={selectedRow?.name}
          showReason={false}
          confirmText="Delete"
          cancelText="Cancel"
          iconColor="#DC2626"
          iconBgColor="#FEE2E2"
          confirmBtnColor="#DC2626"
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => {
            setIsDeleteModalOpen(false);
            setDocuments((prev) =>
              prev.filter((doc) => doc.key !== selectedRow.key),
            );
            notification.success({
              message: "Document Deleted",
              description: `${selectedRow?.name} has been successfully removed.`,
              placement: "topRight",
            });
          }}
        />
      </div>
    </div>
  );
};

export default DriverDetail;
